import React, { useMemo, useState } from 'react';
import { Upload, X, Loader2, Check, AlertTriangle } from 'lucide-react';
import api, { resolveUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Fuzzy match utility — normalize a filename or item name.
const norm = (s) => (s || '').toLowerCase().replace(/\.[a-z0-9]+$/, '').replace(/[_\-]+/g, ' ').replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

// Simple similarity: token set ratio (0..1).
const similarity = (a, b) => {
  const ta = new Set(norm(a).split(' ').filter(Boolean));
  const tb = new Set(norm(b).split(' ').filter(Boolean));
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  ta.forEach((t) => { if (tb.has(t)) inter++; });
  return (2 * inter) / (ta.size + tb.size);
};

const findMatch = (filename, items) => {
  let best = { itemId: '', score: 0 };
  items.forEach((it) => {
    const s = similarity(filename, it.name);
    if (s > best.score) best = { itemId: it.id, score: s };
  });
  return best;
};

/**
 * Props:
 *   open: boolean, onClose: () => void
 *   items: menu items to match against
 *   onApplied: () => void   // called after successful apply so parent can reload
 */
export default function BulkImageUpload({ open, onClose, items, onApplied }) {
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState([]); // {original, url, itemId, score}
  const [dragOver, setDragOver] = useState(false);
  const { toast } = useToast();

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    if (files.length > 50) {
      toast({ title: 'Too many files', description: 'Please select up to 50 at a time.' });
      return;
    }
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f, f.name));
    try {
      const r = await api.post('/admin/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const results = r.data.results || [];
      const newRows = results.map((res) => {
        if (res.error) return { original: res.original_filename, url: '', itemId: '', score: 0, error: res.error };
        const match = findMatch(res.original_filename, items);
        return { original: res.original_filename, url: res.url, itemId: match.itemId, score: match.score };
      });
      setRows((prev) => [...prev, ...newRows]);
      const okCount = newRows.filter((r) => !r.error).length;
      toast({ title: `${okCount} file(s) uploaded`, description: 'Review matches below then Apply.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const applyAll = async () => {
    const toApply = rows.filter((r) => r.url && r.itemId);
    if (toApply.length === 0) {
      toast({ title: 'Nothing to apply', description: 'Match filenames to items first.' });
      return;
    }
    setApplying(true);
    let ok = 0, fail = 0;
    for (const r of toApply) {
      const item = items.find((i) => i.id === r.itemId);
      if (!item) { fail++; continue; }
      try {
        await api.put(`/admin/menu/${item.id}`, {
          name: item.name,
          description: item.description || '',
          price: item.price,
          category: item.category,
          image: r.url,
          available: item.available !== false,
          modifierGroupIds: item.modifierGroupIds || [],
          sortOrder: item.sortOrder ?? 0,
        });
        ok++;
      } catch (err) {
        fail++;
      }
    }
    setApplying(false);
    toast({ title: `Applied ${ok} image(s)`, description: fail > 0 ? `${fail} failed.` : 'Done.' });
    if (ok > 0) {
      onApplied?.();
      setRows([]);
      onClose?.();
    }
  };

  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const setRowItem = (idx, itemId) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, itemId, score: 1 } : r)));

  const matchedCount = useMemo(() => rows.filter((r) => r.itemId && r.url).length, [rows]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bulk-upload-modal">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-xl text-[#0F1C2E]">Bulk Image Upload</h2>
            <p className="text-sm text-gray-500 mt-1">Drop photos in — we'll auto-match them to items by filename.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded" data-testid="bulk-upload-close"><X size={18} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Drop zone */}
          <label
            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`block border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition ${dragOver ? 'border-[#ED2E2E] bg-[#FDF3EC]' : 'border-gray-300 hover:border-[#ED2E2E] hover:bg-[#FDF3EC]/40'}`}
            data-testid="bulk-upload-dropzone">
            <input type="file" multiple accept="image/*" className="hidden"
                   onChange={(e) => handleFiles(e.target.files)} data-testid="bulk-upload-input" />
            {uploading ? (
              <div className="flex flex-col items-center gap-2 text-[#ED2E2E]">
                <Loader2 size={32} className="animate-spin" />
                <div className="font-semibold">Uploading…</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-gray-600">
                <Upload size={32} className="text-[#ED2E2E]" />
                <div className="font-semibold text-[#0F1C2E]">Drop image files here or click to select</div>
                <div className="text-xs text-gray-500">PNG, JPG, WEBP · up to 8 MB each · up to 50 files at a time</div>
                <div className="text-xs text-gray-500 mt-1">Tip: name files like the menu item — e.g. <span className="font-mono">signature-chicken-donair.png</span></div>
              </div>
            )}
          </label>

          {rows.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-600"><b>{matchedCount}</b> of {rows.length} matched · review and adjust below</div>
                <button onClick={() => setRows([])} className="text-xs text-gray-500 hover:text-[#ED2E2E]" data-testid="bulk-upload-clear">Clear list</button>
              </div>
              <div className="space-y-2">
                {rows.map((r, idx) => (
                  <div key={idx} className={`flex items-center gap-3 border rounded-xl p-3 ${r.error ? 'border-red-200 bg-red-50/40' : r.itemId ? 'border-green-200 bg-green-50/30' : 'border-yellow-200 bg-yellow-50/30'}`} data-testid={`bulk-upload-row-${idx}`}>
                    {r.error ? (
                      <div className="w-14 h-14 rounded-lg bg-red-100 flex items-center justify-center text-red-500"><AlertTriangle size={20} /></div>
                    ) : (
                      <img src={resolveUrl(r.url)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-gray-500 truncate">{r.original}</div>
                      {r.error ? (
                        <div className="text-xs text-red-600 mt-1">{r.error}</div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <select value={r.itemId} onChange={(e) => setRowItem(idx, e.target.value)}
                                  className="text-sm px-2 py-1.5 rounded-lg border border-gray-200 bg-white flex-1 focus:outline-none focus:border-[#ED2E2E]"
                                  data-testid={`bulk-upload-select-${idx}`}>
                            <option value="">— Skip / no match —</option>
                            {items.map((it) => (
                              <option key={it.id} value={it.id}>{it.name} · {it.category}</option>
                            ))}
                          </select>
                          {r.itemId && r.score >= 0.5 && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600 font-semibold whitespace-nowrap">
                              <Check size={12} /> {Math.round(r.score * 100)}% match
                            </span>
                          )}
                          {r.itemId && r.score < 0.5 && r.score > 0 && (
                            <span className="text-xs text-yellow-600 font-semibold whitespace-nowrap">Low confidence ({Math.round(r.score * 100)}%)</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeRow(idx)} className="p-2 rounded hover:bg-white text-gray-400 hover:text-red-600"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 bg-gray-50/60 flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-xl text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={applyAll} disabled={applying || matchedCount === 0}
                  className="btn-wrappus text-sm disabled:opacity-50" data-testid="bulk-upload-apply">
            {applying ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {applying ? 'Applying…' : `Apply ${matchedCount} image${matchedCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
